import { Badge, type IconButtonProps, type SxProps, type Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import { type FC, useEffect, useState } from "react";
import type { DrawerTriggerNotification } from "../../contexts/RecommendationsDrawerContext";
import { MOTION, STATUS, SURFACE } from "../../theme/tokens";
import CopilotButton from "../CopilotButton/CopilotButton";
import { InHouseTooltip } from "../InHouseTooltip/InHouseTooltip";

interface DrawerTriggerButtonProps extends Omit<IconButtonProps, "sx"> {
	open?: boolean;
	notification: DrawerTriggerNotification; // required — every call site must compute its own
	sx?: SxProps<Theme>;
	/**
	 * Annie walkthrough anchor (`context/standards/frontend.md`). It already reaches the DOM through the
	 * rest spread below (CopilotButton spreads into an MUI IconButton), but the lint's rule P proves
	 * forwarding by looking for this declaration in this file — a rest spread it cannot see.
	 *
	 * Passed by the caller, never hardcoded here: the Day Schedule and Synergy Staffing render this same
	 * button, and an anchor on all four call sites would spotlight it on pages the step's copy isn't about.
	 */
	"data-annie-target"?: string;
}

/**
 * One-shot arrival pop on the dot when the underlying counts change — mirrors `CallInIndicator`'s
 * "something happened, look here" animation (scale-overshoot + a fading ring) so a growing count
 * doesn't just silently relabel the tooltip. Dropped entirely under reduced motion.
 */
const popSx = (dotColor: string): SystemStyleObject<Theme> => ({
	"@keyframes drawerTriggerDotPop": {
		"0%": { transform: "scale(1) translate(var(--Badge-translate)) translateY(0)" },
		"30%": { transform: "scale(1) translate(var(--Badge-translate)) translateY(-6px)" },
		"50%": { transform: "scale(1) translate(var(--Badge-translate)) translateY(0)" },
		"70%": { transform: "scale(1) translate(var(--Badge-translate)) translateY(-2px)" },
		"100%": { transform: "scale(1) translate(var(--Badge-translate)) translateY(0)" },
	},
	"@keyframes drawerTriggerDotRing": {
		"0%": { boxShadow: `0 0 0 0 ${alpha(dotColor, 0.45)}` },
		"100%": { boxShadow: `0 0 0 5px ${alpha(dotColor, 0)}` },
	},
	animation: `drawerTriggerDotPop ${MOTION.DURATION.HIGHLIGHT_JUMP}ms ${MOTION.EASE_OUT}, drawerTriggerDotRing ${MOTION.DURATION.HIGHLIGHT_JUMP}ms ${MOTION.EASE_OUT}`,
	"@media (prefers-reduced-motion: reduce)": { animation: "none" },
});

/**
 * Wraps `CopilotButton` with the notification-dot affordance for the RecommendationsDrawer trigger:
 * a dot when there's something to look at (unread chat / requests with responses), with a hover
 * tooltip breaking down what it is. Composition mirrors `DayViewContent/DayViewLocationFitler.tsx`
 * (InHouseTooltip > Badge variant="dot" > icon button).
 */
export const DrawerTriggerButton: FC<DrawerTriggerButtonProps> = ({ open, notification, sx, onClick, ...props }) => {
	// Adjusted during render (not a useEffect) so the very first painted frame with new content already
	// carries the pop — an effect would show one un-animated frame first.
	const [lastTooltipText, setLastTooltipText] = useState(notification?.tooltipText);
	const [shouldPop, setShouldPop] = useState(false);
	if (notification?.tooltipText !== lastTooltipText) {
		setShouldPop(!!notification?.tooltipText);
		setLastTooltipText(notification?.tooltipText);
	}

	// Resets after the pop finishes so the *next* change — however soon — gets a genuine off-to-on
	// transition to animate from, rather than silently staying "already popped" forever (unlike
	// `CallInIndicator`, this component doesn't get remounted by virtualization to reset it for us).
	useEffect(() => {
		if (!shouldPop) return;
		const timer = setTimeout(() => setShouldPop(false), MOTION.DURATION.HIGHLIGHT_JUMP);
		return () => clearTimeout(timer);
	}, [shouldPop]);

	if (open) return null; // CopilotButton also self-hides on open, but Badge/Tooltip need a real child to wrap
	// Requests (need action) win over chat (informational) when both are present — see
	// `getDrawerTriggerNotification`'s color rule.
	const dotColor = notification?.color === "success" ? STATUS.SUCCESS.main : STATUS.ERROR.main;
	return (
		<InHouseTooltip title={notification?.tooltipText ?? ""}>
			<Badge
				variant="dot"
				overlap="circular"
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
				invisible={!notification}
				data-testid="drawer-trigger-badge"
				sx={{
					"& .MuiBadge-dot": {
						width: 14,
						height: 14,
						minWidth: 14,
						borderRadius: "50%",
						backgroundColor: dotColor,
						border: `2px solid ${SURFACE}`,
						...(shouldPop ? popSx(dotColor) : {}),
					},
				}}
			>
				<CopilotButton open={open} onClick={onClick} sx={sx} {...props} />
			</Badge>
		</InHouseTooltip>
	);
};

export default DrawerTriggerButton;
